
import React, { useState } from 'react';
import styled from 'styled-components';
import Modal from 'react-modal';
import { FaBrain, FaMobileAlt, FaUsers, FaHospital } from 'react-icons/fa';

const quadrants = [
  {
    icon: FaBrain,
    title: 'Biological',
    details: 'Mindfulness, biofeedback, sleep & nutrition support for brain development.'
  },
  {
    icon: FaMobileAlt,
    title: 'Digital',
    details: 'Adaptive social media, digital detox, gamification for dopamine regulation.'
  },
  {
    icon: FaUsers,
    title: 'Social',
    details: 'AI-moderated peer support and mentorship reduce isolation & stigma.'
  },
  {
    icon: FaHospital,
    title: 'Systemic',
    details: 'Universal screening, anti-stigma campaigns, policy reform to improve access.'
  }
];

const ResearchFoundation = () => {
  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [modalContent, setModalContent] = useState(null);

  const openModal = (quadrant) => {
    setModalContent(quadrant);
    setModalIsOpen(true);
  };

  const closeModal = () => {
    setModalIsOpen(false);
    setModalContent(null);
  };

  return (
    <ResearchSection id="research">
      <Container>
        <SectionHeading>Our Evidence-Based Multi-Pronged Approach</SectionHeading>
        <QuadrantGrid>
          {quadrants.map((quad, index) => (
            <QuadrantCard key={index} onClick={() => openModal(quad)}>
              <IconWrapper>{React.createElement(quad.icon)}</IconWrapper>
              <QuadrantTitle>{quad.title}</QuadrantTitle>
            </QuadrantCard>
          ))}
        </QuadrantGrid>
      </Container>
      {modalContent && (
        <StyledModal
          isOpen={modalIsOpen}
          onRequestClose={closeModal}
          contentLabel="Quadrant Details"
        >
          <ModalIconWrapper>{React.createElement(modalContent.icon)}</ModalIconWrapper>
          <ModalTitle>{modalContent.title}</ModalTitle>
          <ModalText>{modalContent.details}</ModalText>
          <CloseButton onClick={closeModal}>Close</CloseButton>
        </StyledModal>
      )}
    </ResearchSection>
  );
};

export default ResearchFoundation;

const ResearchSection = styled.section`
  background-color: var(--background-light);
`;

const Container = styled.div`
  max-width: 1000px;
  margin: 0 auto;
  padding: 0 20px;
  text-align: center;
`;

const SectionHeading = styled.h2`
  font-size: 2.5rem;
  margin-bottom: 4rem;
  color: var(--primary-blue-dark);

  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const QuadrantGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-template-rows: 1fr 1fr;
  gap: 2rem;
  aspect-ratio: 1 / 1;
  max-width: 700px;
  margin: 0 auto;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    aspect-ratio: unset;
  }
`;

const QuadrantCard = styled.div`
  background: var(--background-dark);
  border-radius: 15px;
  padding: 2rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.05);

  &:hover {
    transform: translateY(-10px);
    box-shadow: 0 10px 30px rgba(74, 144, 226, 0.3);
  }
`;

const IconWrapper = styled.div`
  font-size: 3rem;
  color: var(--primary-blue-light);
  margin-bottom: 1rem;
`;

const QuadrantTitle = styled.h3`
  font-size: 1.5rem;
  color: var(--primary-blue-dark);
  margin: 0;
`;

// Modal Styles
const StyledModal = styled(Modal)`
  position: absolute;
  top: 50%;
  left: 50%;
  right: auto;
  bottom: auto;
  margin-right: -50%;
  transform: translate(-50%, -50%);
  background: #fff;
  padding: 40px;
  border-radius: 15px;
  max-width: 500px;
  width: 90%;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.1);
  text-align: center;
  outline: none;
`;

const ModalIconWrapper = styled.div`
  font-size: 3rem;
  color: var(--primary-blue-light);
  margin-bottom: 1rem;
`;

const ModalTitle = styled.h2`
  font-size: 2rem;
  color: var(--primary-blue-dark);
  margin: 0 0 1rem 0;
`;

const ModalText = styled.p`
  font-size: 1.1rem;
  line-height: 1.6;
  color: var(--text-dark);
  margin-bottom: 2rem;
`;

const CloseButton = styled.button`
  background: var(--primary-blue-light);
  color: white;
  border: none;
  padding: 12px 24px;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.3s ease;

  &:hover {
    background: var(--primary-blue-dark);
  }
`;
